import { useCallback, useEffect, useRef, useState } from "react";
import {
  chatService,
  type ChatAttachment,
  type ChatMessage,
  type ReactionAggregate,
  type ReactionsByMessage,
} from "../services/chatService";
import { useChatSocket } from "../context/ChatSocketContext";

const PAGE = 30;

/** Sortier-Schlüssel: reale Nachrichten nach `seq`, optimistische ans Ende. */
function sortKey(m: ChatMessage): number {
  return m.seq ? Number(m.seq) : Number.POSITIVE_INFINITY;
}
function sortMessages(list: ChatMessage[]): ChatMessage[] {
  return list
    .slice()
    .sort((a, b) => sortKey(a) - sortKey(b) || a.createdAt.localeCompare(b.createdAt));
}

function newUuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
}

export type UseChatMessages = ReturnType<typeof useChatMessages>;

/**
 * Live-Nachrichten-Store einer Conversation. Eigener Reducer (kein useInfiniteRows):
 * getrennte Pfade für prepend (Scroll-up), append/upsert (Live + optimistisches
 * Echo, dedupe per id/clientMsgId) und remove (Soft-Delete-Tombstone). Senden ist
 * optimistisch mit Ack (temp-id → serverId), Reaktionen/Edits/Deletes laufen
 * event-getrieben. Bei (Re)connect wird das sichtbare Fenster neu geladen (Resync).
 */
export function useChatMessages(
  conversationId: string | null,
  currentUserId: string | null,
  currentUserName: string | null,
) {
  const { on, emit, emitAck, connected } = useChatSocket();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [reactions, setReactions] = useState<ReactionsByMessage>({});
  const [typingUserIds, setTypingUserIds] = useState<string[]>([]);
  const [otherReads, setOtherReads] = useState<Record<string, string>>({});
  const [hasMore, setHasMore] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [ready, setReady] = useState(false);

  const typingTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const typingSentAt = useRef(0);
  const typingStopTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ---------------------------------------------------------------- Store-Ops

  const upsert = useCallback((incoming: ChatMessage) => {
    setMessages((prev) => {
      let replaced = false;
      const next = prev.map((m) => {
        if (m.id === incoming.id || (incoming.clientMsgId && m.clientMsgId === incoming.clientMsgId)) {
          replaced = true;
          return { ...incoming, _status: "sent" as const };
        }
        return m;
      });
      if (!replaced) next.push(incoming);
      return sortMessages(next);
    });
  }, []);

  const applyDeleted = useCallback((id: string) => {
    setMessages((prev) =>
      prev.map((m) => (m.id === id ? { ...m, deletedAt: new Date().toISOString() } : m)),
    );
  }, []);

  const applyReaction = useCallback((agg: ReactionAggregate) => {
    setReactions((prev) => {
      const list = (prev[agg.messageId] ?? []).filter((r) => r.emoji !== agg.emoji);
      if (agg.count > 0) list.push({ emoji: agg.emoji, count: agg.count, userIds: [] });
      return { ...prev, [agg.messageId]: list };
    });
  }, []);

  // ---------------------------------------------------------------- Initial load + subscriptions

  useEffect(() => {
    if (!conversationId) {
      setMessages([]);
      setReactions({});
      setTypingUserIds([]);
      setOtherReads({});
      setReady(false);
      return;
    }
    let cancelled = false;
    setReady(false);

    (async () => {
      try {
        await emitAck("chat:join", { conversationId }).catch(() => undefined);
        const batch = await chatService.listMessages(conversationId, { limit: PAGE });
        if (cancelled) return;
        const asc = sortMessages(batch);
        setMessages(asc);
        setHasMore(batch.length === PAGE);
        const ids = asc.map((m) => m.id);
        const rx = await chatService.reactions(conversationId, ids).catch(() => ({}));
        if (!cancelled) setReactions(rx);
        setReady(true);
      } catch {
        if (!cancelled) setReady(true);
      }
    })();

    const offs = [
      on("message:new", (m: ChatMessage) => {
        if (m.conversationId === conversationId) upsert(m);
      }),
      on("message:updated", (m: ChatMessage) => {
        if (m.conversationId === conversationId) upsert(m);
      }),
      on("message:deleted", (p: { id: string; conversationId: string }) => {
        if (p.conversationId === conversationId) applyDeleted(p.id);
      }),
      on("reaction:updated", (agg: ReactionAggregate) => {
        if (agg.conversationId === conversationId) applyReaction(agg);
      }),
      on("typing:update", (p: { conversationId: string; userId: string; isTyping: boolean }) => {
        if (p.conversationId !== conversationId || p.userId === currentUserId) return;
        const timers = typingTimers.current;
        if (p.isTyping) {
          const existing = timers.get(p.userId);
          if (existing) clearTimeout(existing);
          timers.set(
            p.userId,
            setTimeout(() => {
              timers.delete(p.userId);
              setTypingUserIds((ids) => ids.filter((id) => id !== p.userId));
            }, 6000),
          );
          setTypingUserIds((ids) => (ids.includes(p.userId) ? ids : [...ids, p.userId]));
        } else {
          const existing = timers.get(p.userId);
          if (existing) clearTimeout(existing);
          timers.delete(p.userId);
          setTypingUserIds((ids) => ids.filter((id) => id !== p.userId));
        }
      }),
      on("read:update", (p: { conversationId: string; userId: string; lastReadMessageId: string }) => {
        if (p.conversationId !== conversationId || p.userId === currentUserId) return;
        setOtherReads((prev) => ({ ...prev, [p.userId]: p.lastReadMessageId }));
      }),
      on("pin:update", (p: { id: string; conversationId: string; pinned: boolean }) => {
        if (p.conversationId !== conversationId) return;
        setMessages((prev) => prev.map((m) => (m.id === p.id ? { ...m, pinned: p.pinned } : m)));
      }),
    ];

    return () => {
      cancelled = true;
      offs.forEach((off) => off());
      typingTimers.current.forEach((t) => clearTimeout(t));
      typingTimers.current.clear();
      setTypingUserIds([]);
    };
    // `connected` als Dep → bei Reconnect neu joinen + Fenster neu laden (Resync).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, connected, on, upsert, applyDeleted, applyReaction, emitAck, currentUserId]);

  // ---------------------------------------------------------------- Pagination

  const loadOlder = useCallback(async () => {
    if (!conversationId || loadingOlder || !hasMore) return;
    const oldest = messages.find((m) => m.seq);
    if (!oldest) return;
    setLoadingOlder(true);
    try {
      const batch = await chatService.listMessages(conversationId, {
        beforeSeq: oldest.seq,
        limit: PAGE,
      });
      setHasMore(batch.length === PAGE);
      if (batch.length) {
        setMessages((prev) => sortMessages([...batch, ...prev]));
        const ids = batch.map((m) => m.id);
        const rx = await chatService.reactions(conversationId, ids).catch(() => ({}));
        setReactions((prev) => ({ ...prev, ...rx }));
      }
    } finally {
      setLoadingOlder(false);
    }
  }, [conversationId, loadingOlder, hasMore, messages]);

  // ---------------------------------------------------------------- Senden / Bearbeiten

  const send = useCallback(
    async (
      body: string,
      mentionedUserIds: string[] = [],
      attachments: ChatAttachment[] = [],
      replyToId?: string | null,
    ) => {
      if (!conversationId || (!body.trim() && attachments.length === 0)) return;
      const clientMsgId = newUuid();
      const optimistic: ChatMessage = {
        id: `tmp-${clientMsgId}`,
        conversationId,
        seq: "",
        tenantId: "",
        authorUserId: currentUserId ?? "",
        authorName: currentUserName,
        body,
        clientMsgId,
        createdAt: new Date().toISOString(),
        attachments,
        replyToId: replyToId ?? null,
        _status: "sending",
      };
      setMessages((prev) => sortMessages([...prev, optimistic]));
      stopTyping();
      try {
        const res = await emitAck<{ message: ChatMessage }>("message:send", {
          conversationId,
          body,
          clientMsgId,
          mentionedUserIds,
          attachmentIds: attachments.map((a) => a.id),
          replyToId: replyToId ?? undefined,
        });
        if (res?.message) upsert(res.message);
      } catch {
        setMessages((prev) =>
          prev.map((m) => (m.clientMsgId === clientMsgId ? { ...m, _status: "failed" } : m)),
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [conversationId, currentUserId, currentUserName, emitAck, upsert],
  );

  const retry = useCallback(
    (clientMsgId: string) => {
      const msg = messages.find((m) => m.clientMsgId === clientMsgId);
      if (!msg) return;
      setMessages((prev) => prev.filter((m) => m.clientMsgId !== clientMsgId));
      void send(msg.body);
    },
    [messages, send],
  );

  const edit = useCallback(
    async (messageId: string, body: string) => {
      if (!body.trim()) return;
      await emitAck("message:edit", { id: messageId, body }).catch(() =>
        chatService.editMessage(messageId, body),
      );
    },
    [emitAck],
  );

  const remove = useCallback(
    async (messageId: string) => {
      applyDeleted(messageId);
      await emitAck("message:delete", { id: messageId }).catch(() =>
        chatService.deleteMessage(messageId),
      );
    },
    [emitAck, applyDeleted],
  );

  const toggleReaction = useCallback(
    (messageId: string, emoji: string) => {
      emit("reaction:toggle", { messageId, emoji });
    },
    [emit],
  );

  const togglePin = useCallback((messageId: string, pinned: boolean) => {
    // Optimistisch; das pin:update-Event (auch an die eigenen Tabs) bestätigt/korrigiert.
    setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, pinned } : m)));
    (pinned ? chatService.pinMessage(messageId) : chatService.unpinMessage(messageId)).catch(() => {
      setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, pinned: !pinned } : m)));
    });
  }, []);

  const markRead = useCallback(
    (lastReadMessageId: string) => {
      if (!conversationId || !lastReadMessageId) return;
      emit("read:update", { conversationId, lastReadMessageId });
    },
    [conversationId, emit],
  );

  // ---------------------------------------------------------------- Typing

  const notifyTyping = useCallback(() => {
    if (!conversationId) return;
    const now = Date.now();
    if (now - typingSentAt.current > 3000) {
      typingSentAt.current = now;
      emit("typing:start", { conversationId });
    }
    if (typingStopTimer.current) clearTimeout(typingStopTimer.current);
    typingStopTimer.current = setTimeout(() => {
      emit("typing:stop", { conversationId });
      typingSentAt.current = 0;
    }, 5000);
  }, [conversationId, emit]);

  const stopTyping = useCallback(() => {
    if (!conversationId) return;
    if (typingStopTimer.current) {
      clearTimeout(typingStopTimer.current);
      typingStopTimer.current = null;
    }
    if (typingSentAt.current) {
      emit("typing:stop", { conversationId });
      typingSentAt.current = 0;
    }
  }, [conversationId, emit]);

  return {
    messages,
    reactions,
    typingUserIds,
    otherReads,
    hasMore,
    loadingOlder,
    ready,
    loadOlder,
    send,
    retry,
    edit,
    remove,
    toggleReaction,
    togglePin,
    markRead,
    notifyTyping,
    stopTyping,
  };
}
